import request from '@/utils/request.js'

// 用户登录
export function login(data) {
  return request({
    url: '/system/auth/login',
    method: 'post',
    data
  })
}

// 用户登出
export function logout() {
  return request({
    url: '/system/auth/logout',
    method: 'post'
  })
}

/**
 * 根据用户 ID 获取用户详情
 *
 * 对应后端接口：
 * GET /system/user/get
 *
 * @param {number} id 用户编号（必传）
 *
 * @returns {Promise<{
 *   code: number,
 *   msg: string,
 *   data: {
 *     id: number,           // 用户编号
 *     username: string,     // 用户账号
 *     nickname: string,     // 用户昵称
 *     remark?: string,      // 备注
 *
 *     deptId?: number,      // 部门 ID
 *     deptName?: string,    // 部门名称
 *
 *     postIds?: number[],   // 岗位 ID 数组
 *
 *     email?: string,       // 用户邮箱
 *     mobile?: string,      // 手机号码
 *     sex?: number,         // 性别（SexEnum）
 *     avatar?: string,      // 用户头像
 *
 *     status: number,       // 状态（CommonStatusEnum）
 *
 *     loginIp?: string,     // 最后登录 IP
 *     loginDate?: string,   // 最后登录时间（LocalDateTime 字符串）
 *     createTime: string    // 创建时间（LocalDateTime 字符串）
 *   } | null                // 用户不存在时返回 null
 * }>}
 */
export function getUser(id) {
  return request({
    url: '/system/user/get',
    method: 'get',
    params: { id }
  })
}

/**
 * 获取当前登录用户的权限信息
 *
 * 返回数据结构：
 * {
 *   user: {                  // 当前登录用户信息
 *     id: number,            // 用户ID
 *     nickname: string,      // 昵称
 *     avatar: string,        // 头像地址
 *     deptId: number,        // 部门ID
 *     username: string,      // 登录账号
 *     email: string          // 邮箱
 *   },
 *   roles: string[],         // 角色标识列表（如：admin、system_user）
 *   permissions: string[],   // 操作权限标识列表（如：system:user:create）
 *   menus: [                 // 菜单树
 *     {
 *       id: number,
 *       parentId: number,
 *       name: string,        // 菜单名称
 *       path: string,        // 路由地址
 *       component: string,   // 前端组件路径
 *       componentName: string,// 组件名
 *       icon: string,        // 菜单图标
 *       visible: boolean,    // 是否显示
 *       keepAlive: boolean,  // 是否缓存
 *       alwaysShow: boolean, // 是否始终显示
 *       children: []         // 子菜单
 *     }
 *   ]
 * }
 *
 * @returns {Promise<Object>} 登录用户的权限信息
 */
export function getPermissionInfo() {
  return request({
    url: '/system/auth/get-permission-info',
    method: 'get'
  })
}


// 获取用户列表
export function getUserList(params) {
  return request({
    url: '/system/user/page',
    method: 'get',
    params
  })
}

// 获取用户详情
export function getUserDetail(userId) {
  return request({
    url: `/sys/user/${userId}`,
    method: 'get'
  })
}

/**
 * 新增用户
 *
 * 对应后端接口：
 * POST /system/user/create
 *
 * @param {Object} data - 用户创建参数（UserSaveReqVO）
 * @param {string} data.username - 用户账号（必填，4-30 位，数字/字母）
 * @param {string} data.nickname - 用户昵称（必填，<= 30 字）
 * @param {string} [data.remark] - 备注
 // * @param {number} [data.deptId] - 部门 ID
 // * @param {number[]} [data.postIds] - 岗位 ID 集合
 * @param {string} [data.email] - 邮箱
 * @param {string} [data.mobile] - 手机号
 * @param {number} [data.sex] - 性别（参见 SexEnum 男1 女2 未知0）
 * @param {string} [data.avatar] - 用户头像 URL
 * @param {string} data.password - 登录密码（仅创建时必填，4-16 位）
 *
 * @returns {Promise<number>} 新创建用户的 ID
 */
export function createUser(data) {
  return request({
    url: '/system/user/create',
    method: 'post',
    data
  })
}
/**
 * 更新用户信息
 *
 * 对应后端接口：
 * PUT /system/user/update
 *
 * @param {Object} data - 用户更新参数（UserSaveReqVO）
 * @param {number} data.id - 用户 ID（必填）
 * @param {string} data.username - 用户账号（必填，4-30 位，数字/字母）
 * @param {string} data.nickname - 用户昵称（必填，<= 30 字）
 * @param {string} [data.remark] - 备注
 // * @param {number} [data.deptId] - 部门 ID
 // * @param {number[]} [data.postIds] - 岗位 ID 集合
 * @param {string} [data.email] - 邮箱
 * @param {string} [data.mobile] - 手机号
 * @param {number} [data.sex] - 性别（参见 SexEnum）
 * @param {string} [data.avatar] - 用户头像 URL
 *
 * ⚠️ 注意：
 * - 更新用户时【不需要】传 password
 * - 后端通过 id != null 判断为“更新操作”
 *
 * @returns {Promise<void>}
 */
export function updateUser(data) {
  return request({
    url: '/system/user/update',
    method: 'put',
    data
  })
}

/**
 * 删除用户
 *
 * 对应后端接口：
 * DELETE /system/user/delete?id=xxx
 *
 * @param {number} userId - 用户 ID
 * @returns {Promise<boolean>}
 */
export function deleteUser(userId) {
  return request({
    url: '/system/user/delete',
    method: 'delete',
    params: {
      id: userId
    }
  })
}

/**
 * 重置用户密码
 *
 * 对应后端接口：
 * PUT /system/user/update-password
 *
 * @param {Object} data - 请求参数
 * @param {number} data.id - 用户 ID
 * @param {string} data.password - 新密码（4-16 位）
 * @returns {Promise<boolean>}
 */
export function updateUserPassword(data) {
  return request({
    url: '/system/user/update-password',
    method: 'put',
    data
  })
}


/**
 * 批量删除用户
 *
 * 对应后端接口：
 * DELETE /system/user/delete-list
 *
 * 请求示例：
 * /system/user/delete-list?ids=1&ids=2&ids=3
 *
 * @param {number[]} ids - 用户 ID 列表
 * @returns {Promise<boolean>}
 */
export function deleteUserList(ids) {
  // 手动构建查询参数：ids=1&ids=2&ids=3
  const params = new URLSearchParams()
  ids.forEach(id => params.append('ids', id))

  return request({
    url: `/system/user/delete-list?${params.toString()}`,
    method: 'delete'
  })
}


/**
 * 修改用户状态
 *
 * @param {number} id - 用户 ID
 * @param {number} status - 用户状态（0=启用，1=禁用，对应 CommonStatusEnum）
 * @returns {Promise}
 */
export function updateUserStatus(id, status) {
  return request({
    url: '/system/user/update-status',
    method: 'put',
    data: {
      id,
      status
    }
  })
}


export const refreshTokenWithParams = (Authorization) => {
  return request.post('/system/auth/refresh-token?Authorization=' + Authorization + '&tenant-id=' + 1+"&refreshToken="+Authorization)}
